@let loading = (loading$ | async);
@let saving = (saving$ | async);
<div class="sim">
  <div class="sim-header">
    <div class="sim-title-wr">
      <h4 class="sim-title">
        {{ isEdit ? (form?.invoice?.docNumber ? 'Invoice #' + form.invoice.docNumber : 'Invoice') : 'New QuickBooks invoice' }}
      </h4>
      @if(form?.invoice?.status){
        <span class="sim-status" [attr.data-status]="form.invoice.status">{{ form.invoice.status.replace('_', ' ').toLowerCase() }}</span>
      }
      @if(form?.invoice?.lastSyncedAt){
        <span class="sim-synced" title="Kept up to date from QuickBooks; use Sync on the invoice row to refresh it now">Synced {{ form.invoice.lastSyncedAt | date: 'MM/dd/yy h:mm a' }}</span>
      }
      @if(form?.invoice?.qbId){
        <a class="sim-qb-link" href="{{ qbUrl }}/app/invoice?txnId={{ form.invoice.qbId }}" target="_blank" rel="noopener">
          Open in QuickBooks <i class="fas fa-external-link-alt"></i>
        </a>
      }
    </div>
    <button type="button" class="sim-close" [disabled]="saving" (click)="close()" aria-label="Close">
      <i class="fas fa-times"></i>
    </button>
  </div>

  @if(loading || !form){
    <div class="sim-body">
      <app-preloader [type]="'LARGE_LIST'" [count]="4" [customHeight]="'40px'"></app-preloader>
    </div>
  } @else {
    <div class="sim-body">
      <div class="sim-fields">
        <div class="sim-field">
          <span class="sim-label">Customer</span>
          <span class="sim-value">{{ form.customer?.name }}</span>
        </div>
        <div class="sim-field">
          <span class="sim-label">QuickBooks customer</span>
          <!-- New invoice: the truck's customer (one per truck); existing invoice: the one it bills -->
          <span class="sim-value">
            @if(isEdit){
              {{ form.invoice?.qbCustomerName || '—' }}
            } @else {
              {{ form.truck?.qbCustomer?.name || '—' }}
              @if(form.truck?.truck?.unitNumber){
                <span class="sim-muted">· truck {{ form.truck.truck.unitNumber }}</span>
              }
            }
          </span>
        </div>
        <div class="sim-field">
          <span class="sim-label">Bill to / Ship to</span>
          <span class="sim-value">{{ form.customer?.address || 'Customer address in QuickBooks' }}</span>
        </div>
        @for(field of form.customFields; track $index){
          <div class="sim-field">
            <span class="sim-label">{{ field.name }}</span>
            @if(isEdit && readOnly){
              <!-- A read-only invoice: shown as it is in QuickBooks -->
              <span class="sim-value">{{ field.current || '—' }}</span>
            } @else {
              <!-- Editing: what the invoice has, and what saving changes it to -->
              <span class="sim-value" [title]="field.source ? 'From: ' + field.source : ''">
                @if(isEdit && field.current !== field.value){
                  {{ field.current || 'empty' }} on the invoice
                  <span class="sim-muted">· saving sets {{ field.value || 'empty' }}</span>
                } @else {
                  {{ field.value || '—' }}
                }
              </span>
            }
          </div>
        }
        <div class="sim-field">
          <span class="sim-label">Terms</span>
          <app-select
            [list]="form.terms"
            [keyName]="{ key: 'Id', name: 'Name' }"
            [value]="salesTermId"
            [placeholder]="'QuickBooks customer\'s terms'"
            [isReadOnly]="readOnly"
            (onSave)="setSalesTerm($event)"
          ></app-select>
        </div>
        <div class="sim-field">
          <span class="sim-label">Due date</span>
          <!-- Empty: QuickBooks takes it from the terms -->
          <app-date
            [startKey]="'dueDate'"
            [parent]="due"
            [options]="{ fullFormat: true, disabled: readOnly, placeholder: 'From terms' }"
            [placeholder]="'From terms'"
            (onSave)="setDueDate($event.dueDate)"
          ></app-date>
        </div>
        <div class="sim-field">
          <span class="sim-label">Bill email</span>
          <!-- The customer's emails to pick from, or any other address typed in -->
          <app-select
            [list]="emailOptions"
            [keyName]="{ key: 'value', name: 'value' }"
            [value]="billEmail"
            [placeholder]="'Email to send the invoice to'"
            [isReadOnly]="readOnly"
            [autocomplete]="'none'"
            [options]="{ noFirstOption: true, allowCustom: true }"
            (onCustom)="billEmail = $event"
            (onSave)="billEmail = $event"
          ></app-select>
        </div>
      </div>

      @if(form.disconnected){
        <p class="sim-new-hint sim-partly-deleted-hint">QuickBooks is disconnected, so this invoice is read only. Reconnect QuickBooks in company settings to edit it.</p>
      }
      @if(hasNewLines && !readOnly){
        <p class="sim-new-hint">{{ newLinesHint }}</p>
      }
      @if(hasDeletedLines){
        <p class="sim-new-hint sim-deleted-hint">
          @if(allLinesDropped && !readOnly){
            Every line bills services that were deleted from the order, so nothing is left to bill. Delete the invoice instead of saving it.
          } @else {
            Red lines bill services that were deleted from the order after this invoice was created. Saving the invoice will delete them from it.
          }
        </p>
      }
      @if(hasPartlyDeletedLines){
        <p class="sim-new-hint sim-partly-deleted-hint">Yellow lines had services deleted from the order: they are updated from the services left{{ hasYellowWithNewLabor ? ', and labor added to the order since joins them' : '' }}. Check the description and amount before saving.</p>
      }
      <table class="sim-lines">
        <thead>
          <tr>
            <th class="sim-col-item">Item</th>
            <th>Description</th>
            <th class="sim-col-qty">Qty</th>
            <th class="sim-col-rate">Rate</th>
            <th class="sim-col-amount">Amount</th>
            <th class="sim-col-remove"></th>
          </tr>
        </thead>
        <tbody>
          @for(line of lines; track $index; let i = $index){
            <tr
              [id]="'sim-line-' + i"
              [class.sim-line-new]="isBlue(line)"
              [class.sim-line-deleted]="isDroppedOnSave(line)"
              [class.sim-line-partly-deleted]="line.deletedServices && !isDroppedOnSave(line)"
              [title]="isDroppedOnSave(line) ? 'Its services were deleted from the order: saving deletes this line' : line.deletedServices ? line.deletedServices + (line.deletedServices === 1 ? ' service' : ' services') + ' on this line deleted from the order: updated from the ones left' + (line.isNew ? ', with the labor added since' : '') : line.changedServices ? 'Updated from the order: ' + line.changedServices + (line.changedServices === 1 ? ' service' : ' services') + ' on this line changed since the invoice was saved' : line.isNew && line.qbLineId ? 'Labor added to the order since the invoice was created was added to this line' : ''"
            >
              <td>
                <app-select
                  [list]="form.items"
                  [keyName]="{ key: 'Id', name: 'Name' }"
                  [value]="line.itemId"
                  [invalid]="savePressed && !line.itemId && !isDroppedOnSave(line)"
                  [isReadOnly]="readOnly"
                  [options]="{ noFirstOption: true }"
                  (onSave)="onItemChange(line, $event)"
                ></app-select>
              </td>
              <td>
                <textarea class="sim-input" [rows]="descriptionRows(line)" [disabled]="readOnly" [(ngModel)]="line.description" maxlength="4000" placeholder="Description"></textarea>
              </td>
              <td>
                <app-input
                  [type]="'text'"
                  [value]="line.qty"
                  [placeholder]="'—'"
                  [isReadOnly]="readOnly"
                  [options]="{ reqField: savePressed && !isQtyValid(line) && !isDroppedOnSave(line) }"
                  (onInput)="line.qty = $event; onQtyRateChange(line)"
                ></app-input>
              </td>
              <td>
                <app-input
                  [type]="'text'"
                  [value]="line.rate"
                  [placeholder]="'—'"
                  [isReadOnly]="readOnly"
                  [options]="{ reqField: savePressed && !isRateValid(line) && !isDroppedOnSave(line) }"
                  (onInput)="line.rate = $event; onQtyRateChange(line)"
                ></app-input>
              </td>
              <td>
                <!-- qty x rate when both are set, typed by hand otherwise -->
                <app-input
                  [type]="'text'"
                  [value]="line.amount"
                  [placeholder]="'0.00'"
                  [isReadOnly]="readOnly || hasQtyRate(line)"
                  [options]="{ reqField: savePressed && !!line.itemId && isQtyValid(line) && isRateValid(line) && !isLineValid(line) && !isDroppedOnSave(line) }"
                  (onInput)="line.amount = $event"
                ></app-input>
              </td>
              <td>
                @if(!readOnly){
                  <button type="button" class="sim-remove" (click)="removeLine(i)" aria-label="Remove line">
                    <i class="fa-solid fa-trash-can"></i>
                  </button>
                }
              </td>
            </tr>
          } @empty {
            <tr>
              <td colspan="6" class="sim-empty">
                {{ isEdit ? 'This invoice has no lines.' : 'All services of this order are already invoiced. Add a line to bill something else.' }}
              </td>
            </tr>
          }
        </tbody>
      </table>

      <div class="sim-lines-footer">
        @if(!readOnly){
          <div class="sim-add-wr">
            <button type="button" class="sim-add" (click)="addLine()"><i class="fa-solid fa-plus"></i> Add line</button>
            @if(canAddShopFee){
              <button type="button" class="sim-add" [disabled]="shopFeeAdding" (click)="addShopFee()"
                title="Adds the shop fee as the last line: the invoice's labor hours at the item's QuickBooks price">
                <i class="fa-solid fa-plus"></i> Shop Fee
              </button>
            }
          </div>
        }
        <div class="sim-total">
          <span class="sim-label">Total</span>
          <span class="sim-total-value">{{ total | currency }}</span>
          @if(isEdit && form.invoice?.balance !== undefined){
            <span class="sim-label">Balance {{ form.invoice.balance / 100 | currency }}</span>
          }
        </div>
      </div>

      <div class="sim-field">
        <span class="sim-label">Message on invoice</span>
        <textarea class="sim-input" rows="2" [disabled]="readOnly" [(ngModel)]="customerMemo" maxlength="1000"></textarea>
      </div>
    </div>

    <div class="sim-footer">
      @if(isEdit && props?.canUpdate && !isPaid && !form.disconnected){
        <button type="button" class="sim-delete" [disabled]="saving" (click)="deleteInvoice()">
          <i class="fa-solid fa-trash-can"></i> Delete invoice
        </button>
      }
      <button type="button" class="sim-cancel" [disabled]="saving" (click)="close()">Cancel</button>
      @if(!readOnly){
        <!-- As freight's invoice modal: Send left of Save, same look. QuickBooks emails the invoice to the
             bill email after saving it -->
        <button type="button" class="sim-save sim-save-send" [disabled]="saving || !lines.length || allLinesDropped" (click)="save(true)"
          [title]="billEmail?.trim() ? 'Save, then email the invoice to ' + billEmail.trim() : 'Add the email to send the invoice to'">
          @if(saving && sending){
            <i class="fa-solid fa-spinner fa-spin"></i>
          }
          {{ isEdit ? 'Save and Send' : 'Create and Send' }}
        </button>
        <button type="button" class="sim-save" [disabled]="saving || !lines.length || allLinesDropped" (click)="save()">
          @if(saving && !sending){
            <i class="fa-solid fa-spinner fa-spin"></i>
          }
          {{ isEdit ? 'Save to QuickBooks' : 'Create in QuickBooks' }}
        </button>
      }
    </div>
  }
</div>
